import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/header';
import ConfigCard from '../user-configuration/components/ConfigCard';

const CheckIcon = ({ isActive }) => {
  return (
    <svg width={15} height={15} viewBox="0 0 15 15" fill="none">
      <circle cx="7.5" cy="7.5" r="7.5" fill={isActive ? '#2C81C3' : '#d1d5db'} />
      {isActive && (
        <path
          d="M11.0271 6.22915L6.58364 9.93287C6.49112 10.01 6.36571 10.0533 6.23496 10.0533C6.1042 10.0533 5.97879 10.01 5.88627 9.93287L4.07969 8.42492C3.99254 8.34688 3.94509 8.24365 3.94734 8.13699C3.9496 8.03033 4.00138 7.92856 4.09179 7.85313C4.18219 7.7777 4.30415 7.73449 4.43198 7.73261C4.55981 7.73073 4.68353 7.77032 4.77706 7.84304L6.23561 9.0595L10.3297 5.64672..."
          fill="white"
        />
      )}
    </svg>
  );
};


const HomePage = () => {
  const nav = useNavigate();
  const [orgData, setOrgData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openAccordion, setOpenAccordion] = useState(null);
  const [isSyncing, setIsSyncing] = useState(false);

  const handleAccordionToggle = (tenantId) => {
    setOpenAccordion(openAccordion === tenantId ? null : tenantId);
  };

  /* ───────────────────────────────────────────────────────── fetch org data */
  // useEffect(() => {
  //   const jdOrgId = localStorage.getItem('jd_organization_id');
  //   const internalId = localStorage.getItem('origo_org_id');
  //   const token = localStorage.getItem('origo_access_token');
  //   const orgId = jdOrgId || internalId;

  //   if (!orgId || !token) {
  //     alert('Authorization missing ‑ please log in again.');
  //     nav('/home');
  //     return;
  //   }
  //   fetchOrgData(orgId, token);
  // }, [nav]);

  useEffect(() => {
    const run = async () => {
      try {
        const token = localStorage.getItem('origo_access_token');
        const orgId = localStorage.getItem('origo_org_id');
  
        if (!token || !orgId) {
          alert('Authorization missing – please log in again.');
          nav('/home');
          return;
        }
  
        await fetchOrgData(orgId, token);
      } catch (e) {
        console.error('Failed to load org data:', e);
        alert('Failed to load data – please login again.');
        nav('/home');
      }
    };
  
    run();
  }, [nav]);
  

  const fetchOrgData = async (orgId, token) => {
    try {
      const { data } = await axios.get(
        `${process.env.REACT_APP_ORIGO_BASE_URL}origo/get-org-data/${orgId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
  
      setOrgData(data.data);
    } catch (e) {
      console.error('Fetch org data failed', e?.response?.data || e.message);
      alert('Fetch failed – please reconnect.');
      nav('/home');
    } finally {
      setLoading(false);
    }
  };
  
  /* ───────────────────────────────────────────────────────── quick actions */
  const handleDisconnect = async () => {
    try {
      const orgId = localStorage.getItem('origo_org_id');
      const token = localStorage.getItem('origo_access_token');

      if (token && orgId) {
        await axios.delete(
          `${process.env.REACT_APP_ORIGO_BASE_URL}oauth-jd/disconnect`,
          { headers: { Authorization: `Bearer ${token}` }, data: { organization_id: orgId } }
        );
      }

      /* leave creds but wipe tokens */
      const username = localStorage.getItem('origo_username');
      const password = localStorage.getItem('origo_password');

      localStorage.removeItem('origo_access_token');
      localStorage.removeItem('origo_org_id');
      localStorage.removeItem('jd_organization_id');
      localStorage.removeItem('origo_auth_url');

      /* immediately kick off JD OAuth again if we still have creds */
      if (username && password) {
        const { data } = await axios.post(
          `${process.env.REACT_APP_ORIGO_BASE_URL}oauth-jd/login`,
          { username, password, restart: false }
        );
        const url = data?.data?.authorizationUrl;
        if (url) {
          localStorage.setItem('origo_auth_url', url);
          nav('/home');
        }
      }

      nav('/home');
    } catch (e) {
      console.error('Disconnect failed', e);
      alert('Failed to disconnect.');
    }
  };

  // Top of component
  const [syncInfo, setSyncInfo] = useState(null);

  // After orgData loads, fetch last-sync:
  useEffect(() => {
    if (!orgData) return;
    (async () => {
      try {
        const token = localStorage.getItem('origo_access_token');
        const orgId = localStorage.getItem('origo_org_id');

        const url = `${process.env.REACT_APP_ORIGO_BASE_URL}origo/last-sync`;
        console.log('Calling API:', url);

        const { data } = await axios.get(url, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          params: {
            organization_id: orgId,
          },
        });

        setSyncInfo(data.data);
        console.log('Last sync info fetched:', data.data);
      } catch (e) {
        console.error('Last-sync fetch error:', e);
      }
    })();
  }, [orgData]);

  const formatDate = (dateString) => {
    try {
      return new Intl.DateTimeFormat(undefined, {
        year: 'numeric',
        month: 'short',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true,
        timeZoneName: 'short'
      }).format(new Date(dateString));
    } catch (err) {
      console.error('Date formatting error:', err);
      return '—';
    }
  };


  // Sync handler
  const handleSync = async () => {
    if (syncInfo?.currently_in_sync || isSyncing) {
      alert('Sync is already in progress.');
      return;
    }

    try {
      setIsSyncing(true); // 🔒 Start syncing

      const orgId = localStorage.getItem('origo_org_id');
      const token = localStorage.getItem('origo_access_token');

      if (!token || !orgId) {
        alert('Authorization token or organization ID is missing. Please log in again.');
        nav('/home');
        return;
      }

      await axios.get(
        `${process.env.REACT_APP_ORIGO_BASE_URL}origo/sync`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      alert('Sync started successfully!');
    } catch (e) {
      console.error('Sync failed', e);
      alert('Sync failed – try again.');
    } finally {
      try {
        const orgId = localStorage.getItem('origo_org_id');
        const token = localStorage.getItem('origo_access_token');

        const { data } = await axios.get(
          `${process.env.REACT_APP_ORIGO_BASE_URL}origo/last-sync`,
          {
            headers: { Authorization: `Bearer ${token}` },
            params: { organization_id: orgId }
          }
        );
        setSyncInfo(data); // ✅ Update sync info
      } catch (err) {
        console.error('Error refreshing sync info', err);
      }

      setIsSyncing(false); // ✅ Sync done → Re-enable button
    }
  };


  /* ───────────────────────────────────────────────────────── render */
  return (
    <div className="min-h-screen px-4 py-6 bg-[#f3f6f9] dark:bg-[#121212] transition-colors duration-300">
      <Header />
      <div className="h-px bg-[#d5d5d5] dark:bg-gray-700 mb-6" />

      {/* connection banner */}
      <div className="flex justify-between items-center px-4 py-3 border border-[#099d96] rounded bg-[#daf4f0] dark:bg-[#0d3d39]">
        <p className="text-lg font-semibold text-black dark:text-white">
          John Deere connection status:&nbsp;
          <span className="text-[#099d96] dark:text-[#34d1c5]">Connected</span>
        </p>
        <button
          onClick={handleDisconnect}
          className="bg-[#f06548] text-white px-4 py-2 rounded-full text-sm hover:opacity-90 transition"
        >
          Disconnect now
        </button>
      </div>

      {/* quick actions */}
      <div className="flex flex-col items-start gap-5 px-4">
        <div className="text-black dark:text-white font-poppins text-sm font-semibold leading-[150%]">
          Last data sync time: {syncInfo?.last_successful_sync ? formatDate(syncInfo.last_successful_sync) : '—'}
        </div>

        <button
          onClick={handleSync}
          disabled={isSyncing || syncInfo?.currently_in_sync}
          className={`flex justify-center items-center gap-2.5 py-[0.9375rem] px-[1.875rem] rounded-full bg-white dark:bg-gray-800 text-black dark:text-white font-poppins text-sm font-semibold leading-[150%] shadow transition ${isSyncing || syncInfo?.currently_in_sync
            ? 'opacity-50 cursor-not-allowed'
            : 'cursor-pointer hover:opacity-90'
            }`}
        >
          {isSyncing || syncInfo?.currently_in_sync ? 'Sync in progress…' : 'Sync now'}
        </button>
      </div>

      {/* tenants & sensors */}
      <div className="mt-8 bg-white dark:bg-[#1e1e1e] pb-6 transition-colors">
        <div className="flex justify-between items-center p-4">
          <h2 className="text-black dark:text-white text-lg font-semibold px-4">
            Data being transferred to John Deere
          </h2>
          <button
            onClick={() => nav('/choose-data')}
            className="flex items-center gap-1 hover:bg-blue-700 bg-[#2696F9] text-white px-4 py-1 rounded-full text-sm font-medium"
          >
            Modify data
          </button>
        </div>

        {loading && (
          <p className="text-center text-sm mb-4 text-gray-600 dark:text-gray-400">
            Loading…
          </p>
        )}

        {!loading && !orgData?.tenants?.length && (
          <p className="text-center px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
            No tenant data available.
          </p>
        )}

        {orgData?.tenants?.map((tenant, tIdx) => (
          <div
                key={tenant.id}
                className="collapse collapse-arrow border border-gray-200 dark:border-gray-700 rounded-lg mb-4"
              >
                <input
                  type="checkbox"
                  checked={openAccordion === tenant.id}
                  onChange={() => handleAccordionToggle(tenant.id)}
                />
                <div className="collapse-title text-lg font-medium bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white">
                  {tenant.origo_tenant_name}
                  <span className="text-sm font-normal text-gray-600 dark:text-gray-400 ml-2">
                    ({tenant.stations.length} stations)
                  </span>
                </div>
                <div className="collapse-content bg-gray-50 dark:bg-gray-700">
                  <div className="flex flex-wrap gap-6 pt-4">
                    {tenant.stations.map((station, sIdx) => (
                      <ConfigCard
                        key={station.id}
                        title={station.origo_station_name}
                        total={station.sensors.length}
                        selected={
                          station.sensors.filter((s) => s.is_active).length
                        }
                        options={station.sensors.map((s) => ({
                          id: s.id,
                          label: s.origo_sensor_name,
                          checked: s.is_active,
                        }))}
                        stationData={{
                          organization_id: localStorage.getItem('origo_org_id'),
                          tenant_id: tenant.id,
                          station_id: station.id,
                        }}
                        isEditable={false}
                      />
                    ))}
                  </div>
                </div>
              </div>
        ))}
      </div>
    </div>
  );

};

export default HomePage;

